import React, { useState, useCallback, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const darkenHex = (hex, amount = 40) => {
  const h = (hex || '#0e7572').replace('#', '');
  const num = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  const r = Math.max(0, (num >> 16) - amount);
  const g = Math.max(0, ((num >> 8) & 0xff) - amount);
  const b = Math.max(0, (num & 0xff) - amount);
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`;
};

// ─── helpers ────────────────────────────────────────────────────────────────

const uid = () => `step_${Date.now()}_${Math.random()?.toString(36)?.slice(2, 7)}`;

const makeStep = (index) => ({
  id: uid(),
  title: `Page ${index + 1}`,
  description: '',
  fieldIds: [],
});

const defaultConfig = (existing) => ({
  enabled: true,
  steps: existing?.steps?.length ? existing?.steps : [makeStep(0)],
  progressStyle: existing?.progressStyle || 'bar',
  progressPosition: existing?.progressPosition || 'top',
  showStepTitles: existing?.showStepTitles !== false,
  showStepCount: existing?.showStepCount !== false,
  nextButtonText: existing?.nextButtonText || 'Next',
  prevButtonText: existing?.prevButtonText || 'Previous',
  submitButtonText: existing?.submitButtonText || 'Submit',
  allowStepJump: existing?.allowStepJump || false,
  validateOnNext: existing?.validateOnNext !== false,
  // conversationalMode is now a top-level layoutMode in the form builder
});

// ─── element type catalogue (mirrors ElementLibrary categories) ──────────────
const ELEMENT_GROUPS = [
  {
    label: 'Input Fields',
    items: [
      { id: 'text',      name: 'Text Input',   icon: 'Type',            type: 'input' },
      { id: 'email',     name: 'Email',         icon: 'Mail',            type: 'input' },
      { id: 'phone',     name: 'Phone',         icon: 'Phone',           type: 'input' },
      { id: 'number',    name: 'Number',        icon: 'Hash',            type: 'input' },
      { id: 'url',       name: 'URL',           icon: 'Link',            type: 'input' },
      { id: 'textarea',  name: 'Text Area',     icon: 'AlignLeft',       type: 'textarea' },
    ],
  },
  {
    label: 'Selection',
    items: [
      { id: 'dropdown',  name: 'Dropdown',      icon: 'ChevronDown',     type: 'select' },
      { id: 'radio',     name: 'Radio Buttons', icon: 'Circle',          type: 'radio' },
      { id: 'checkbox',  name: 'Checkboxes',    icon: 'CheckSquare',     type: 'checkbox' },
      { id: 'toggle',    name: 'Toggle Switch', icon: 'ToggleLeft',      type: 'toggle' },
      { id: 'slider',    name: 'Slider',        icon: 'SlidersHorizontal', type: 'slider' },
    ],
  },
  {
    label: 'Advanced',
    items: [
      { id: 'file',      name: 'File Upload',   icon: 'Upload',          type: 'file' },
      { id: 'signature', name: 'Signature',     icon: 'PenTool',         type: 'signature' },
      { id: 'hidden',    name: 'Hidden Field',  icon: 'EyeOff',          type: 'hidden' },
    ],
  },
  {
    label: 'Layout & Content',
    items: [
      { id: 'heading',   name: 'Heading',       icon: 'Heading',         type: 'content' },
      { id: 'paragraph', name: 'Paragraph',     icon: 'AlignLeft',       type: 'content' },
      { id: 'divider',   name: 'Divider',       icon: 'Minus',           type: 'layout' },
      { id: 'section',   name: 'Section',       icon: 'Box',             type: 'layout' },
    ],
  },
  {
    label: 'Media',
    items: [
      { id: 'image',     name: 'Image',         icon: 'Image',           type: 'media' },
      { id: 'button',    name: 'Button',        icon: 'MousePointer',    type: 'button' },
    ],
  },
];

// ─── component ───────────────────────────────────────────────────────────────

/**
 * MultiStepPanel
 *
 * Completely self-contained. State is seeded ONCE from `initialConfig` at mount
 * via useState lazy initializer — the prop is NEVER read again after that.
 * All updates go through `notify` which calls `onUpdateConfig` without causing
 * any re-render in this component beyond the one triggered by `setConfig`.
 *
 * onAddElement(elementDef, stepId) — called when user picks an element type
 * to add directly to a specific page.
 */
const MultiStepPanel = ({ formElements = [], initialConfig, onUpdateConfig, onAddElement, onDeleteElement, globalTheme = {}, activeStep }) => {
  // ── local state ─────────────────────────────────────────────────────────────
  const [config, setConfig] = useState(() => initialConfig || { enabled: false });
  const [expandedStep, setExpandedStep] = useState(null);
  // Refs to each step card so we can scroll it into view on tab-click sync
  const stepRefs = useRef({});

  // When the canvas tab row changes the active preview step, expand that page
  // card in the PAGES panel and scroll it into view.
  useEffect(() => {
    if (activeStep == null) return;
    const steps = config?.steps || [];
    if (!steps?.length) return;
    const step = steps[Math.min(activeStep, steps?.length - 1)];
    if (!step) return;
    setExpandedStep(step?.id);
    setTimeout(() => {
      const el = stepRefs?.current?.[step?.id];
      if (el) el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 80);
  }, [activeStep]); // eslint-disable-line react-hooks/exhaustive-deps
  // stepId whose "add element" picker is open, or null
  const [addPickerStep, setAddPickerStep] = useState(null);
  // drag-and-drop state
  const [dragState, setDragState] = useState({ draggingId: null, overId: null });

  // ── helpers ─────────────────────────────────────────────────────────────────
  const notify = useCallback((next) => {
    setConfig(next);
    if (typeof onUpdateConfig === 'function') {
      onUpdateConfig(next);
    }
  }, [onUpdateConfig]);

  const enabled = Boolean(config?.enabled);
  const steps = config?.steps || [];

  // ── toggle ──────────────────────────────────────────────────────────────────
  const handleToggle = useCallback(() => {
    if (enabled) {
      notify({ ...config, enabled: false });
    } else {
      notify(defaultConfig(config));
    }
  }, [enabled, config, notify]);

  // ── field helpers ────────────────────────────────────────────────────────────
  const unassigned = (formElements || [])?.filter((el) => {
    if (el?.type === 'button') return false;
    return !steps?.some((s) => (s?.fieldIds || [])?.includes(el?.id));
  });

  const fieldLabel = (id) => {
    const f = (formElements || [])?.find((el) => el?.id === id);
    return f ? (f?.label || f?.name || id) : id;
  };

  const fieldIcon = (id) => {
    const f = (formElements || [])?.find((el) => el?.id === id);
    const map = {
      input: 'Type', select: 'ChevronDown', checkbox: 'CheckSquare',
      radio: 'Circle', file: 'Upload', signature: 'PenTool', textarea: 'AlignLeft',
    };
    return map?.[f?.type] || 'Box';
  };

  // ── page management ──────────────────────────────────────────────────────────
  const addPage = () => {
    const step = makeStep(steps?.length);
    const next = { ...config, steps: [...steps, step] };
    notify(next);
    setExpandedStep(step?.id);
  };

  const removePage = (id) => {
    if (steps?.length <= 1) return;
    const next = { ...config, steps: steps?.filter((s) => s?.id !== id) };
    notify(next);
    if (expandedStep === id) setExpandedStep(null);
  };

  const updatePage = (id, changes) => {
    const next = { ...config, steps: steps?.map((s) => s?.id === id ? { ...s, ...changes } : s) };
    notify(next);
  };

  const movePage = (from, to) => {
    if (to < 0 || to >= steps?.length) return;
    const arr = [...steps];
    const [item] = arr?.splice(from, 1);
    arr?.splice(to, 0, item);
    notify({ ...config, steps: arr });
  };

  // ── drag-and-drop handlers ───────────────────────────────────────────────────
  const handleDragStart = (e, stepId) => {
    setDragState({ draggingId: stepId, overId: null });
    e.dataTransfer.effectAllowed = 'move';
    e?.dataTransfer?.setData('text/plain', stepId);
  };

  const handleDragOver = (e, stepId) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragState?.draggingId !== stepId) {
      setDragState((prev) => ({ ...prev, overId: stepId }));
    }
  };

  const handleDragLeave = (e, stepId) => {
    // Only clear overId if leaving the element entirely (not entering a child)
    if (!e?.currentTarget?.contains(e?.relatedTarget)) {
      setDragState((prev) => ({ ...prev, overId: null }));
    }
  };

  const handleDrop = (e, targetStepId) => {
    e?.preventDefault();
    const { draggingId } = dragState;
    setDragState({ draggingId: null, overId: null });
    if (!draggingId || draggingId === targetStepId) return;
    const fromIdx = steps?.findIndex((s) => s?.id === draggingId);
    const toIdx = steps?.findIndex((s) => s?.id === targetStepId);
    if (fromIdx === -1 || toIdx === -1) return;
    movePage(fromIdx, toIdx);
  };

  const handleDragEnd = () => {
    setDragState({ draggingId: null, overId: null });
  };

  const assignField = (fieldId, stepId) => {
    const next = {
      ...config,
      steps: steps?.map((s) => ({
        ...s,
        fieldIds: s?.id === stepId
          ? [...(s?.fieldIds || []), fieldId]
          : (s?.fieldIds || [])?.filter((id) => id !== fieldId),
      })),
    };
    notify(next);
  };

  const removeField = (fieldId, stepId) => {
    const next = {
      ...config,
      steps: steps?.map((s) =>
        s?.id === stepId ? { ...s, fieldIds: (s?.fieldIds || [])?.filter((id) => id !== fieldId) } : s
      ),
    };
    notify(next);
  };

  const updateField = (key, value) => {
    notify({ ...config, [key]: value });
  };

  // ── add element directly to a page ──────────────────────────────────────────
  const handlePickElement = (elementDef, stepId) => {
    setAddPickerStep(null);
    if (typeof onAddElement === 'function') {
      onAddElement(elementDef, stepId);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* ── Enable toggle ── */}
      <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-center gap-3">
          <Icon name="Layers" size={18} color="var(--color-primary)" className="flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">Multi-Step Form</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Split your form into multiple pages.
            </p>
          </div>
          {/* Toggle button — plain button, no prop dependency */}
          <button
            type="button"
            onClick={handleToggle}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            className={[
              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full',
              'transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              enabled ? 'bg-primary' : 'bg-gray-300',
            ]?.join(' ')}
            aria-pressed={enabled}
            aria-label={enabled ? 'Disable multi-step form' : 'Enable multi-step form'}
          >
            <span
              className={[
                'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow',
                'transition-transform duration-200',
                enabled ? 'translate-x-6' : 'translate-x-1',
              ]?.join(' ')}
            />
          </button>
        </div>
        {/* Status text below toggle */}
        <p className={`text-xs mt-2 font-medium ${enabled ? 'text-primary' : 'text-muted-foreground'}`}>
          {enabled ? '✓ Multi-step is ON' : 'Multi-step is OFF — click toggle to enable'}
        </p>
      </div>
      {/* ── Disabled placeholder ── */}
      {!enabled && (
        <div className="text-center py-10 border-2 border-dashed border-border rounded-lg">
          <Icon name="Layers" size={36} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-sm font-medium text-muted-foreground">Multi-step is disabled</p>
          <p className="text-xs text-muted-foreground mt-1">Use the toggle above to enable it</p>
        </div>
      )}
      {/* ── Enabled content ── */}
      {enabled && (
        <>
          {/* Pages list */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">
                Pages ({steps?.length})
              </h4>
            </div>

            {steps?.map((step, idx) => (
              <div
                key={step?.id}
                ref={(el) => { stepRefs.current[step?.id] = el; }}
                draggable
                onDragStart={(e) => handleDragStart(e, step?.id)}
                onDragOver={(e) => handleDragOver(e, step?.id)}
                onDragLeave={(e) => handleDragLeave(e, step?.id)}
                onDrop={(e) => handleDrop(e, step?.id)}
                onDragEnd={handleDragEnd}
                className={[
                  'border rounded-lg overflow-hidden bg-card transition-all duration-150',
                  dragState?.draggingId === step?.id
                    ? 'opacity-40 border-primary border-dashed'
                    : dragState?.overId === step?.id
                    ? 'border-primary border-2 shadow-md'
                    : 'border-border',
                ]?.join(' ')}
              >
                {/* Page header */}
                <div
                  className="flex items-center gap-2 p-2.5 bg-muted/20 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => setExpandedStep(expandedStep === step?.id ? null : step?.id)}
                >
                  {/* Drag handle */}
                  <div
                    className="flex-shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors"
                    title="Drag to reorder"
                    onClick={(e) => e?.stopPropagation()}
                  >
                    <Icon name="GripVertical" size={14} />
                  </div>

                  {/* Reorder */}
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => { e?.stopPropagation(); movePage(idx, idx - 1); }}
                      disabled={idx === 0}
                      className="p-0.5 disabled:opacity-30 hover:text-primary"
                    >
                      <Icon name="ChevronUp" size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e?.stopPropagation(); movePage(idx, idx + 1); }}
                      disabled={idx === steps?.length - 1}
                      className="p-0.5 disabled:opacity-30 hover:text-primary"
                    >
                      <Icon name="ChevronDown" size={12} />
                    </button>
                  </div>

                  {/* Badge */}
                  <div className="w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {idx + 1}
                  </div>

                  {/* Title */}
                  <input
                    type="text"
                    value={step?.title}
                    onChange={(e) => { e?.stopPropagation(); updatePage(step?.id, { title: e?.target?.value }); }}
                    onClick={(e) => e?.stopPropagation()}
                    className="flex-1 text-sm font-medium bg-transparent border-none outline-none text-foreground min-w-0"
                    placeholder={`Page ${idx + 1}`}
                  />

                  {/* Field count */}
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    {(step?.fieldIds || [])?.length} field{(step?.fieldIds || [])?.length !== 1 ? 's' : ''}
                  </span>

                  {/* Delete */}
                  {steps?.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => { e?.stopPropagation(); removePage(step?.id); }}
                      className="p-1 text-destructive hover:bg-destructive/10 rounded transition-colors flex-shrink-0"
                    >
                      <Icon name="Trash2" size={12} />
                    </button>
                  )}

                  <Icon
                    name={expandedStep === step?.id ? 'ChevronUp' : 'ChevronDown'}
                    size={13}
                    color="var(--color-muted-foreground)"
                    className="flex-shrink-0"
                  />
                </div>

                {/* Expanded body */}
                {expandedStep === step?.id && (
                  <div className="p-3 space-y-3 border-t border-border bg-muted/5">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground block mb-1">
                        Page description (optional)
                      </label>
                      <input
                        type="text"
                        value={step?.description || ''}
                        onChange={(e) => updatePage(step?.id, { description: e?.target?.value })}
                        placeholder="Describe this page..."
                        className="w-full px-2.5 py-1.5 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-primary text-foreground"
                      />
                    </div>

                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1.5">Fields on this page</p>

                      {(step?.fieldIds || [])?.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-2 border border-dashed border-border rounded">
                          No fields assigned yet
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {(step?.fieldIds || [])?.map((fid) => (
                            <div key={fid} className="flex items-center gap-2 px-2 py-1.5 bg-muted/30 rounded text-xs">
                              <Icon name={fieldIcon(fid)} size={12} color="var(--color-muted-foreground)" />
                              <span className="flex-1 truncate text-foreground">{fieldLabel(fid)}</span>
                              <button
                                type="button"
                                onClick={() => removeField(fid, step?.id)}
                                className="text-muted-foreground hover:text-destructive transition-colors"
                              >
                                <Icon name="X" size={10} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Assign existing unassigned field */}
                      {unassigned?.length > 0 && (
                        <select
                          value=""
                          onChange={(e) => { if (e?.target?.value) assignField(e?.target?.value, step?.id); }}
                          className="w-full mt-2 px-2 py-1.5 text-xs bg-background border border-input rounded-md text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="">+ Assign existing field to this page…</option>
                          {unassigned?.map((f) => (
                            <option key={f?.id} value={f?.id}>{f?.label || f?.name || f?.id}</option>
                          ))}
                        </select>
                      )}

                      {/* ── Add NEW element directly to this page ── */}
                      <div className="mt-2 relative">
                        <button
                          type="button"
                          onClick={() => setAddPickerStep(addPickerStep === step?.id ? null : step?.id)}
                          className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 border border-dashed border-primary/50 rounded-md text-xs font-medium text-primary hover:bg-primary/5 hover:border-primary transition-colors"
                        >
                          <Icon name="Plus" size={12} />
                          Add new element to this page
                        </button>

                        {/* Element type picker dropdown */}
                        {addPickerStep === step?.id && (
                          <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-background border border-border rounded-lg shadow-lg overflow-hidden">
                            <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/20">
                              <span className="text-xs font-semibold text-foreground">Choose element type</span>
                              <button
                                type="button"
                                onClick={() => setAddPickerStep(null)}
                                className="text-muted-foreground hover:text-foreground"
                              >
                                <Icon name="X" size={12} />
                              </button>
                            </div>
                            <div className="max-h-64 overflow-y-auto p-2 space-y-2">
                              {ELEMENT_GROUPS?.map((group) => (
                                <div key={group?.label}>
                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1 mb-1">
                                    {group?.label}
                                  </p>
                                  <div className="grid grid-cols-2 gap-1">
                                    {group?.items?.map((el) => (
                                      <button
                                        key={el?.id}
                                        type="button"
                                        onClick={() => handlePickElement(el, step?.id)}
                                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs text-foreground hover:bg-primary/10 hover:text-primary transition-colors text-left"
                                      >
                                        <Icon name={el?.icon} size={12} color="var(--color-muted-foreground)" />
                                        <span className="truncate">{el?.name}</span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={addPage}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border-2 border-dashed border-primary/40 rounded-lg text-sm font-medium text-primary hover:bg-primary/5 hover:border-primary transition-colors"
            >
              <Icon name="Plus" size={16} />
              Add New Page
            </button>
          </div>

          {/* Unassigned fields section */}
          {unassigned?.length > 0 && (
            <div className="border border-amber-200 rounded-lg overflow-hidden">
              {/* Header */}
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200">
                <Icon name="AlertTriangle" size={13} color="#b45309" className="flex-shrink-0" />
                <p className="text-xs text-amber-700 font-semibold flex-1">
                  {unassigned?.length} field{unassigned?.length !== 1 ? 's' : ''} not assigned to any page
                </p>
              </div>
              {/* Field list */}
              <div className="bg-amber-50/50 divide-y divide-amber-100">
                {unassigned?.map((field) => (
                  <div key={field?.id} className="flex items-center gap-2 px-3 py-2">
                    <Icon name={fieldIcon(field?.id)} size={12} color="#92400e" className="flex-shrink-0" />
                    <span className="flex-1 text-xs text-amber-900 truncate">
                      {field?.label || field?.name || field?.id}
                    </span>
                    <button
                      type="button"
                      title="Delete this field"
                      onClick={() => {
                        if (typeof onDeleteElement === 'function') {
                          onDeleteElement(field?.id);
                        }
                      }}
                      className="p-1 rounded text-amber-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                    >
                      <Icon name="Trash2" size={12} />
                    </button>
                  </div>
                ))}
              </div>
              {/* Footer hint */}
              <div className="px-3 py-1.5 bg-amber-50 border-t border-amber-100">
                <p className="text-xs text-amber-600">
                  Unassigned fields appear on the last page. Assign them above or delete unused ones.
                </p>
              </div>
            </div>
          )}

          {/* Progress settings */}
          <div className="space-y-3 p-3 bg-muted/20 rounded-lg border border-border">
            <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">Progress Indicator</h4>

            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Style</label>
              <select
                value={config?.progressStyle || 'bar'}
                onChange={(e) => updateField('progressStyle', e?.target?.value)}
                className="w-full px-2.5 py-1.5 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-primary text-foreground"
              >
                <option value="bar">Progress Bar</option>
                <option value="steps">Step Indicators (dots)</option>
                <option value="numbered">Numbered Steps</option>
                <option value="none">None</option>
                <option value="footer-bar">Footer Bar</option>
              </select>
            </div>

            {(config?.progressStyle || 'bar') === 'footer-bar' && (
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">
                  Bottom corner radius: {config?.footerBorderRadius ?? 0}px
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={0}
                    max={24}
                    step={1}
                    value={config?.footerBorderRadius ?? 0}
                    onChange={(e) => updateField('footerBorderRadius', Number(e?.target?.value))}
                    className="flex-1 h-1.5 accent-primary cursor-pointer"
                  />
                  <button
                    type="button"
                    onClick={() => updateField('footerBorderRadius', 0)}
                    className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2 flex-shrink-0"
                    title="Reset to square (0 px)"
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}

            {(config?.progressStyle || 'bar') !== 'footer-bar' && (
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Position</label>
              <select
                value={config?.progressPosition || 'top'}
                onChange={(e) => updateField('progressPosition', e?.target?.value)}
                className="w-full px-2.5 py-1.5 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-primary text-foreground"
              >
                <option value="top">Top of form</option>
                <option value="bottom">Bottom of form</option>
              </select>
            </div>
            )}

            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={config?.showStepTitles !== false}
                  onChange={(e) => updateField('showStepTitles', e?.target?.checked)}
                  className="rounded border-input"
                />
                Show page titles
              </label>
              <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={config?.showStepCount !== false}
                  onChange={(e) => updateField('showStepCount', e?.target?.checked)}
                  className="rounded border-input"
                />
                Show page count
              </label>
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="space-y-3 p-3 bg-muted/20 rounded-lg border border-border">
            <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">Navigation Buttons</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Next button</label>
                <input
                  type="text"
                  value={config?.nextButtonText || 'Next'}
                  onChange={(e) => updateField('nextButtonText', e?.target?.value)}
                  placeholder="Next"
                  className="w-full px-2.5 py-1.5 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-primary text-foreground"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Previous button</label>
                <input
                  type="text"
                  value={config?.prevButtonText || 'Previous'}
                  onChange={(e) => updateField('prevButtonText', e?.target?.value)}
                  placeholder="Previous"
                  className="w-full px-2.5 py-1.5 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-primary text-foreground"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Submit button (last page)</label>
              <input
                type="text"
                value={config?.submitButtonText || 'Submit'}
                onChange={(e) => updateField('submitButtonText', e?.target?.value)}
                placeholder="Submit"
                className="w-full px-2.5 py-1.5 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-primary text-foreground"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={config?.validateOnNext !== false}
                  onChange={(e) => updateField('validateOnNext', e?.target?.checked)}
                  className="rounded border-input"
                />
                Validate fields before advancing
              </label>
              <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={config?.allowStepJump || false}
                  onChange={(e) => updateField('allowStepJump', e?.target?.checked)}
                  className="rounded border-input"
                />
                Allow jumping to any page
              </label>
            </div>
          </div>

          {/* Preview */}
          {config?.progressStyle !== 'none' && steps?.length > 0 && (
            <div className="p-3 bg-muted/20 rounded-lg border border-border">
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3">Preview</h4>

              {config?.progressStyle === 'bar' && (
                <div className="space-y-1">
                  {config?.showStepCount && (
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Page 1 of {steps?.length}</span>
                      <span>{Math.round((1 / steps?.length) * 100)}%</span>
                    </div>
                  )}
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${(1 / steps?.length) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {config?.progressStyle === 'steps' && (
                <div className="flex items-center gap-1">
                  {steps?.map((step, idx) => (
                    <React.Fragment key={step?.id}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${idx === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                        {idx === 0 ? <Icon name="Check" size={12} /> : idx + 1}
                      </div>
                      {idx < steps?.length - 1 && (
                        <div className={`flex-1 h-0.5 ${idx === 0 ? 'bg-primary' : 'bg-muted'}`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}

              {config?.progressStyle === 'numbered' && (
                <div className="flex items-center gap-2 flex-wrap">
                  {steps?.map((step, idx) => (
                    <div
                      key={step?.id}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${idx === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                    >
                      <span>{idx + 1}</span>
                      {config?.showStepTitles && <span>{step?.title}</span>}
                    </div>
                  ))}
                </div>
              )}

              {config?.progressStyle === 'footer-bar' && (() => {
                const footerBarColor = globalTheme?.footerColor || darkenHex(globalTheme?.accentColor || '#0e7572', 40);
                const footerRadius = config?.footerBorderRadius ?? 0;
                return (
                  <div
                    className="rounded-md border border-border overflow-hidden"
                    style={{
                      borderBottomLeftRadius: `${footerRadius}px`,
                      borderBottomRightRadius: `${footerRadius}px`,
                    }}
                  >
                    <div
                      className="h-14 flex items-center justify-center"
                      style={{ backgroundColor: globalTheme?.backgroundColor || '#ffffff' }}
                    >
                      <p className="text-[10px] text-muted-foreground">Form content</p>
                    </div>
                    <div
                      className="h-5 flex items-center justify-between px-3"
                      style={{ backgroundColor: footerBarColor }}
                    >
                      <span className="text-[8px] font-medium opacity-70" style={{ color: '#ffffff' }}>
                        Page 1 of {steps?.length}
                      </span>
                      <div className="flex gap-1">
                        <div className="h-3 px-1.5 rounded text-[7px] font-medium flex items-center bg-white/20" style={{ color: '#ffffff' }}>
                          Prev
                        </div>
                        <div className="h-3 px-1.5 rounded text-[7px] font-medium flex items-center bg-white/20" style={{ color: '#ffffff' }}>
                          Next
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MultiStepPanel;
